'use client';

import { useCallback } from 'react';
import {
  ApplicationMcpServersSection,
  toasterApi,
} from '@terra/ui-design-system';
import {
  useApplicationMcpServers,
  useAttachMcpServer,
  useDetachMcpServer,
  useTenantMcpServers,
} from '../../hooks/api/useMcpServers';
import type { McpServerResponseDto } from '@terra/shared-contracts-mcp-servers';
import { useTranslations } from 'next-intl';
import { getMcpServerDisplayName } from '@terra/shared-utils';

type ApplicationMcpServersTabProps = {
  applicationId: string;
};

export function ApplicationMcpServersTab({
  applicationId,
}: ApplicationMcpServersTabProps) {
  const t = useTranslations('settings.mcpServerForm');
  const {
    servers: attachedServers,
    isLoading: attachedLoading,
    invalidate,
  } = useApplicationMcpServers({ applicationId });
  const { servers: allTenantServers, isLoading: tenantLoading } =
    useTenantMcpServers();
  const attachMutation = useAttachMcpServer();
  const detachMutation = useDetachMcpServer();
  const tenantServers = allTenantServers.filter((server) =>
    server.allowedTargetTypes.includes('APPLICATION'),
  );
  const attachedIds = new Set(attachedServers.map((link) => link.mcpServerId));
  const handleToggle = useCallback(
    async ({
      server,
      checked,
    }: {
      server: McpServerResponseDto;
      checked: boolean;
    }) => {
      try {
        const displayName = getMcpServerDisplayName(server);

        if (checked) {
          await attachMutation.mutateAsync({
            applicationId,
            mcpServerId: server.id,
            enabled: true,
          });
          toasterApi.success({
            title: t('applicationAttachSuccess', { name: displayName }),
            description: '',
          });
        } else {
          await detachMutation.mutateAsync({
            applicationId,
            mcpServerId: server.id,
          });
          toasterApi.success({
            title: t('applicationDetachSuccess', { name: displayName }),
            description: '',
          });
        }

        await invalidate();
      } catch (error: unknown) {
        console.error(
          new Error('Failed to update MCP attachment', {
            cause: error instanceof Error ? error : new Error(String(error)),
          }),
        );
        toasterApi.error({
          title: t('applicationAttachmentError'),
          description: t('tryAgain'),
        });
      }
    },
    [applicationId, attachMutation, detachMutation, invalidate, t],
  );

  return (
    <ApplicationMcpServersSection
      servers={tenantServers}
      attachedServerIds={attachedIds}
      hasTenantServers={allTenantServers.length > 0}
      isLoading={attachedLoading || tenantLoading}
      isMutating={attachMutation.isPending || detachMutation.isPending}
      onToggle={(input) => void handleToggle(input)}
    />
  );
}
